/**
 * 移动工具（V 键，PS 最基础的工具之一）。
 *
 * 之前只在 registry.ts 里登记了工具栏按钮/快捷键，从没接上真正"拖拽
 * 移动图层像素"这条逻辑——选中移动工具在画布上拖拽什么都不会发生。
 *
 * 跟自由变换（transform-tool.ts）不同：不需要 Enter 提交，松手就落盘
 * （真实 Photoshop 的 Move 工具就是这样，没有"编辑中"的悬置状态）；
 * 也不需要手柄，整块图层内容跟手平移。位移永远是整数像素，直接复用
 * 裁剪同款的 `shiftLayerPixels`——纯行拷贝，没有重采样，不需要 wasm
 * 内核，同步完成。
 */
import type { Document, GroupLayer, Rect } from '../../document/types';
import { TILE, tileKey, type LayerId } from '../../engine/tile/key';
import { shiftLayerPixelsForMove } from '../../engine/tile/shift';
import { findLayer, walkLayers } from '../../document/draft';
import { compositeLayerSubset } from '../../app/composite';
import type { AppStore } from '../../app/store';
import type { ToolManager } from './registry';
import { SNAP_RELEASE_MULTIPLIER, SNAP_THRESHOLD_SCREEN_PX, drawSnapGuideLines, snapAxis } from './snap-guides';

interface Session {
  /** 这次拖拽要挪动的图层——活动图层是组时，是组内全部子孙图层的 id
   * （组自己没有 tile，真正要搬像素的是里面的叶子图层），否则就是这一个。 */
  readonly layers: readonly LayerId[];
  readonly bounds: Rect;
  readonly preview: HTMLCanvasElement | null;
  readonly start: { x: number; y: number };
  dx: number;
  dy: number;
  /** 当前吸附命中的目标位置（文档坐标）——画布边缘/中心或参考线，
   * 没命中该轴则为 null。渲染层据此画对齐提示线（同 Photoshop 的
   * 智能参考线，见 `drawOverlay`）。 */
  snapX: number | null;
  snapY: number | null;
}

export interface MoveDeps {
  readonly store: AppStore;
  readonly tools: ToolManager;
  readonly requestRender: () => void;
  readonly setHiddenLayer: (id: string | null) => void;
  /** "视图→对齐"开关；缺省当作开启（跟 Photoshop 默认一致）。 */
  readonly snapEnabled?: () => boolean;
  /** 当前视口缩放，把屏幕像素的吸附阈值换算成文档像素；缺省当作 1。 */
  readonly viewportScale?: () => number;
}

export class MoveToolController {
  readonly #deps: MoveDeps;
  #session: Session | null = null;

  constructor(deps: MoveDeps) {
    this.#deps = deps;
  }

  get active(): boolean {
    return this.#session !== null;
  }

  /** 拖拽中的当前位移（吸附后）；没有会话时为 null。状态栏提示和测试都读它。 */
  get delta(): { readonly dx: number; readonly dy: number } | null {
    const session = this.#session;
    return session === null ? null : { dx: session.dx, dy: session.dy };
  }

  /** 当前吸附命中的目标位置（文档坐标）；没有会话时为 null，某一轴
   * 没命中则该轴为 null。`drawOverlay` 据此画对齐提示线，测试也读它。 */
  get snapGuides(): { readonly x: number | null; readonly y: number | null } | null {
    const session = this.#session;
    return session === null ? null : { x: session.snapX, y: session.snapY };
  }

  /** 指针处理。当前工具不是移动工具时返回 false，让调用方交给别的工具。 */
  handlePointer(phase: 'down' | 'move' | 'up' | 'double', point: { x: number; y: number }): boolean {
    if (this.#deps.tools.active.id !== 'move') return false;
    const store = this.#deps.store;

    if (phase === 'down') {
      // 移动的是"当前真正活动的图层"，不管它是什么 kind——不能用
      // `activeRasterLayerId()`（那是画笔/仿制图章这类必须落像素到
      // raster 图层的绘制类工具专用的入口，活动图层不是 raster 时会
      // 回退成"最上层 raster 图层"）。移动操作本身只是按图层 id 读写
      // TileStore 的 tile，跟图层 kind 无关——文本图层提交后一样在
      // TileStore 里有真实像素（`rasterizeText` 写的）。用错了入口的
      // 后果：文字/形状图层提交后，即使图层面板显示它是活动图层，移动
      // 工具在画布上点/拖也会被悄悄导向别的图层（或者压根没有别的
      // raster 图层时直接吞掉指针事件），表现为"选中了但拖不动/点不中"。
      const layer = store.activeLayerId;
      if (layer === null) return true; // 是我们的工具但没有可移动的图层，吞掉不动
      const document = store.document;
      const found = document === null ? null : findLayer(document.root, layer);
      // 活动图层是「组」时，组自己在 TileStore 里没有 tile（像素只归叶子
      // 图层），真正要挪动的是组内全部子孙图层——之前这里只按 `layer`
      // 这一个 id 去找 tile，组永远查不到任何像素，会话照样能开起来
      // （`bounds`/`preview` 都是空的），拖拽和松手全程静默什么也不发生，
      // 表现为"选中了一个组，移动工具却拖不动"。
      const group: GroupLayer | null = found !== null && found.layer.kind === 'group' ? found.layer : null;
      const layers: readonly LayerId[] =
        group === null ? [layer] : [...walkLayers(group)].map((entry) => entry.layer.id);
      const bounds = this.#contentBoundsUnion(layers);
      const preview =
        group === null || document === null
          ? this.#flattenPreview(layer, bounds)
          : this.#flattenGroupPreview(document, layer, bounds);
      this.#session = {
        layers,
        bounds,
        preview,
        start: point,
        dx: 0,
        dy: 0,
        snapX: null,
        snapY: null,
      };
      this.#deps.setHiddenLayer(layer); // 组自己的 id 就够了——`compositeGroup` 命中 skipLayerId 直接跳过整棵子树
      this.#deps.requestRender();
      return true;
    }

    const session = this.#session;
    if (session === null) return true;

    if (phase === 'move') {
      let dx = Math.round(point.x - session.start.x);
      let dy = Math.round(point.y - session.start.y);
      if (this.#deps.snapEnabled?.() !== false) {
        [dx, dy] = this.#snap(session, dx, dy);
      } else {
        session.snapX = null;
        session.snapY = null;
      }
      session.dx = dx;
      session.dy = dy;
      this.#deps.requestRender();
      return true;
    }
    if (phase === 'up') {
      this.#commit();
      return true;
    }
    return true;
  }

  /** 文档空间叠加：拖动中的实时预览（松手前原图层已被 setHiddenLayer 隐藏），
   * 命中吸附目标时叠加对齐提示线（画布边缘/中心或参考线，跟 Photoshop
   * 的智能参考线一样——不然用户只能凭感觉猜自己是不是真的对齐了）。 */
  drawOverlay(ctx: CanvasRenderingContext2D, viewportScale = 1): void {
    const session = this.#session;
    if (session === null) return;
    if (session.preview !== null) {
      ctx.save();
      ctx.translate(session.dx, session.dy);
      ctx.drawImage(session.preview, session.bounds.x, session.bounds.y);
      ctx.restore();
    }

    const size = this.#deps.store.document?.size;
    if (size === undefined) return;
    drawSnapGuideLines(ctx, session.snapX, session.snapY, size, viewportScale);
  }

  #commit(): void {
    const session = this.#session;
    if (session === null) return;
    this.#session = null;
    this.#deps.setHiddenLayer(null);

    if (session.dx === 0 && session.dy === 0) {
      this.#deps.requestRender(); // 没挪动，不留历史
      return;
    }
    const store = this.#deps.store;
    const document = store.document;
    if (document === null) {
      this.#deps.requestRender();
      return;
    }

    const label = '移动图层';
    const transaction = store.beginPixelTransaction(label);
    // shiftLayerPixelsForMove 的 offsetX/Y 定义是 new(x,y) = old(x+offsetX, y+offsetY)，
    // 拖拽的位移量 dx/dy 是"新内容相对旧内容挪到哪"，符号正好相反。
    // 组的情况下 `session.layers` 是组内每个叶子图层各自的 id，逐个搬——
    // 都记在同一个 transaction/同一条历史里，撤销时整组一起退回去，不是
    // 拆成一堆图层各自的历史条目
    const touched: Array<{ tx: number; ty: number }> = [];
    for (const layerId of session.layers) {
      touched.push(
        ...shiftLayerPixelsForMove(store.tiles, layerId, -session.dx, -session.dy, transaction),
      );
    }
    store.commitPixels(label, transaction.end());
    store.invalidatePixels(touched);
    this.#deps.requestRender();
  }

  /**
   * 把拖拽位移吸附到画布边缘/中心与参考线（「视图→对齐」，DESIGN.md §6.5 补充）。
   * X/Y 各自独立吸附——横向吸附到一条竖直参考线时，纵向不受影响，反之亦然。
   */
  #snap(session: Session, dx: number, dy: number): [number, number] {
    const document = this.#deps.store.document;
    if (document === null) {
      session.snapX = null;
      session.snapY = null;
      return [dx, dy];
    }
    const scale = this.#deps.viewportScale?.() ?? 1;
    const threshold = SNAP_THRESHOLD_SCREEN_PX / scale;
    const releaseThreshold = (SNAP_THRESHOLD_SCREEN_PX * SNAP_RELEASE_MULTIPLIER) / scale;
    const { bounds } = session;

    const guideX: number[] = [];
    const guideY: number[] = [];
    for (const guide of document.guides) {
      if (guide.axis === 'vertical') guideX.push(guide.position);
      else guideY.push(guide.position);
    }

    // session.snapX/snapY 是这一轴上一轮的命中结果，传进去当"粘滞"的
    // 候选——上一轮没命中（null）就是纯新搜索，跟原来行为一样。
    const snappedX = snapAxis({
      raw: dx,
      boundsStart: bounds.x,
      boundsSize: bounds.width,
      docSize: document.size.width,
      guidePositions: guideX,
      threshold,
      releaseThreshold,
      stickyTarget: session.snapX,
    });
    const snappedY = snapAxis({
      raw: dy,
      boundsStart: bounds.y,
      boundsSize: bounds.height,
      docSize: document.size.height,
      guidePositions: guideY,
      threshold,
      releaseThreshold,
      stickyTarget: session.snapY,
    });

    // target 是命中的绝对目标位置（比如画布水平中线 = document.size.width/2），
    // 提示线要画在这条绝对位置上，不是 snappedX.value 这个位移量本身
    session.snapX = snappedX.target;
    session.snapY = snappedY.target;

    return [snappedX.value, snappedY.value];
  }

  /**
   * 图层实际有像素的精确外接矩形——逐像素扫 alpha，不是量块坐标、也不按
   * 画布尺寸裁一刀。
   *
   * 这里踩过两个真实 bug：
   * 1. 最早的版本把范围夹到 `[0, docWidth) x [0, docHeight)`：只要图层
   *    之前被移到过画布外（`shiftLayerPixelsForMove` 已经保证那部分像素
   *    还留着，没有真的丢），下一次拖拽开始时，这个函数量出来的"内容
   *    范围"却把画布外那部分直接砍掉了——拖拽预览会画出缺一块的图。
   * 2. 改成量"块坐标"外接矩形（`tx*TILE` 到 `(tx+1)*TILE`）之后画布外的
   *    部分不再丢了，但块坐标是按 256 对齐的，图层内容一旦跨块存放
   *    （比如刚被拖过一次，现在横跨两个块但都没填满），量出来的矩形会比
   *    实际内容大一圈——`#snap` 拿 `bounds.x+width`/`bounds.x+width/2`
   *    当"内容右边界/中线"去跟画布边缘/中心比对齐，被这圈虚高的余量
   *    偏移几个像素之后，可能刚好落进吸附阈值，凑巧吸附到一个跟真实
   *    内容边界无关的位置——表现为「来回拖两次，净位移不是零」，跟一开始
   *    那个"被裁剪"的错觉是同一类症状但成因不同（这次是吸附偏移，不是
   *    像素真的丢了），靠 e2e 反复拖拽 + 逐像素比对复现。
   *
   * 逐像素扫描确保量出来的永远是内容真正的边界，两个坑都不会再踩。
   */
  #contentBounds(layer: LayerId): Rect {
    const tiles = this.#deps.store.tiles;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const tile of tiles.residentTilesOf(layer, 0)) {
      const data = tile.data;
      if (data === null) continue;
      const originX = tile.key.tx * TILE;
      const originY = tile.key.ty * TILE;
      for (let ly = 0; ly < TILE; ly++) {
        for (let lx = 0; lx < TILE; lx++) {
          if (data[(ly * TILE + lx) * 4 + 3] === 0) continue;
          const x = originX + lx;
          const y = originY + ly;
          if (x < minX) minX = x;
          if (y < minY) minY = y;
          if (x + 1 > maxX) maxX = x + 1;
          if (y + 1 > maxY) maxY = y + 1;
        }
      }
    }
    if (!Number.isFinite(minX)) return { x: 0, y: 0, width: 0, height: 0 };
    return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
  }

  /** `#contentBounds` 的多图层版本——移动一个组时，内容范围是组内每个
   * 叶子图层各自内容范围的并集，不是随便挑一个。没有像素的图层（比如
   * 组内嵌套的子组自己）贡献一个空 rect，直接跳过，不参与并集。 */
  #contentBoundsUnion(layers: readonly LayerId[]): Rect {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const layer of layers) {
      const b = this.#contentBounds(layer);
      if (b.width <= 0 || b.height <= 0) continue;
      if (b.x < minX) minX = b.x;
      if (b.y < minY) minY = b.y;
      if (b.x + b.width > maxX) maxX = b.x + b.width;
      if (b.y + b.height > maxY) maxY = b.y + b.height;
    }
    if (!Number.isFinite(minX)) return { x: 0, y: 0, width: 0, height: 0 };
    return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
  }

  /**
   * 移动一个组时的拖拽预览——跟 `#flattenPreview`（单图层，直接拷贝原始
   * tile 像素，不参与合成）不一样：组内子图层之间本来就有混合模式/
   * 不透明度/裁剪蒙版等相对关系，随手把每个子图层的像素摆一起会画错。
   * 复用 `compositeLayerSubset`（"合并图层"用的同一个函数）：把 `[groupId]`
   * 当成临时根组的唯一子节点丢给它，它内部用 `findLayer` 换出来的就是
   * 这个组*原本*的 `Layer` 对象——嵌套结构原样保留，混合/裁剪跟正常
   * 显示完全同一条合成代码路径，不是另起一套简化版。
   */
  #flattenGroupPreview(document: Document, groupId: LayerId, bounds: Rect): HTMLCanvasElement | null {
    if (typeof window === 'undefined' || bounds.width <= 0 || bounds.height <= 0) return null;
    const canvas = window.document.createElement('canvas');
    canvas.width = bounds.width;
    canvas.height = bounds.height;
    const ctx = canvas.getContext('2d');
    if (ctx === null) return null;
    const { pixels, width: fullWidth } = compositeLayerSubset(document, this.#deps.store.tiles, [groupId]);
    const image = ctx.createImageData(bounds.width, bounds.height);
    for (let y = 0; y < bounds.height; y++) {
      const srcStart = ((bounds.y + y) * fullWidth + bounds.x) * 4;
      const dstStart = y * bounds.width * 4;
      image.data.set(pixels.subarray(srcStart, srcStart + bounds.width * 4), dstStart);
    }
    ctx.putImageData(image, 0, 0);
    return canvas;
  }

  #flattenPreview(layer: LayerId, bounds: Rect): HTMLCanvasElement | null {
    if (typeof document === 'undefined' || bounds.width <= 0 || bounds.height <= 0) return null;
    const canvas = document.createElement('canvas');
    canvas.width = bounds.width;
    canvas.height = bounds.height;
    const ctx = canvas.getContext('2d');
    if (ctx === null) return null;
    const image = ctx.createImageData(bounds.width, bounds.height);
    const store = this.#deps.store.tiles;
    for (let y = 0; y < bounds.height; y++) {
      const docY = bounds.y + y;
      const ty = Math.floor(docY / TILE);
      const originY = ty * TILE;
      for (let x = 0; x < bounds.width; x++) {
        const docX = bounds.x + x;
        const tx = Math.floor(docX / TILE);
        const originX = tx * TILE;
        const tile = store.get(tileKey(layer, tx, ty));
        const data = tile?.data;
        if (data == null) continue;
        // bounds.x/y 现在可能是负数（ADR-0012：图层内容允许延伸到画布原点
        // 之外），docX/docY 跟着可能为负——不能再用 `docY % TILE` 这种截断
        // 取模（对负数是错的，`-10 % 256 === -10` 不是 `246`），改成减去
        // 已经算出来的块原点，对任意符号都对。
        const from = ((docY - originY) * TILE + (docX - originX)) * 4;
        const to = (y * bounds.width + x) * 4;
        image.data[to] = data[from]!;
        image.data[to + 1] = data[from + 1]!;
        image.data[to + 2] = data[from + 2]!;
        image.data[to + 3] = data[from + 3]!;
      }
    }
    ctx.putImageData(image, 0, 0);
    return canvas;
  }
}
